import {
  SortingFn,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect } from "react";

import { extractSubdomainAndBaseAsync } from "~/utils/domainLazy";

import { formatNumber } from "../../../helpers/helpers";
import styles from "../SiteSpeedTable.module.scss";
import { ensureTrailingDot } from "../helpers";
import { DnsRecordData, DnsRecordsTableProps } from "../types";

const NameCell: React.FC<{ name: string }> = ({ name }) => {
  const [text, setText] = React.useState<string>("@");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { subdomain } = await extractSubdomainAndBaseAsync(
        String(name || ""),
      );
      if (!cancelled) setText(subdomain || "@");
    })();

    return () => {
      cancelled = true;
    };
  }, [name]);

  return <div className={styles.contentCellText}>{text}</div>;
};

const columnHelper = createColumnHelper<DnsRecordData>();

const sortingConfig = [
  { id: "type", desc: false },
  { id: "nameFQDN", desc: false },
];

export default function DnsRecordsTable({
  rows,
  renderActions,
  className,
  domainName,
}: DnsRecordsTableProps) {
  // custom sorting fn for nameFQDN -> show records without subdomain first
  const sortNameFQDN: SortingFn<DnsRecordData> = (a, b) => {
    const domainFormattedName = domainName
      ? ensureTrailingDot(domainName)
      : undefined;

    const aIsMain = a.original.nameFQDN === domainFormattedName;
    const bIsMain = b.original.nameFQDN === domainFormattedName;

    // priority for nameFQDN === domainFormattedName
    if (aIsMain && !bIsMain) return -1;
    if (!aIsMain && bIsMain) return 1;

    // else -> common lexical priority
    return a.original.nameFQDN.localeCompare(b.original.nameFQDN);
  };

  const columns = React.useMemo(
    () => [
      columnHelper.accessor("type", {
        header: "Type",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("nameFQDN", {
        header: "Name",
        cell: (info) => <NameCell name={String(info.getValue() ?? "")} />,
        sortingFn: sortNameFQDN,
      }),
      columnHelper.accessor("content", {
        header: "Content",
        cell: (info) => (
          <div className={styles.contentCellText}>{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor("ttl", {
        header: "TTL",
        cell: (info) => formatNumber(info.getValue()),
      }),
      ...[
        columnHelper.display({
          id: "actions",
          header: "",
          cell: (ctx) => renderActions?.(ctx.row.original) ?? null,
        }),
      ],
    ],
    [renderActions],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting: sortingConfig },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className={styles.siteSpeedTable}>
      <table className={`${styles.siteSpeedTableTable} ${className ?? ""}`}>
        <thead>
          <tr>
            {table.getFlatHeaders().map((header) => (
              // @ts-expect-error TS2339 non-existing className
              <th key={header.id} className={styles.defaultHeaderClass}>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              // @ts-expect-error TS2339 non-existing className
              className={row.original.isModified ? styles.modifiedRow : ""}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={
                    cell.column.id === "content" ? styles.contentCell : ""
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
